/*
 * Copyright (c) 2019.
 * This file is part of the Compo B2B application.
 * (c) Compo soft - Vladimir Ganturin <gun2rin@gmail.com>
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import {Routes} from '@angular/router';
import {AuthGuardCustomerService, AuthGuardMenuService, AuthGuardService, GuestGuardService} from 'compob2b-auth';
import {environment} from '../../environments/environment';
import {translations} from '../../environments/translations.ru';


import {SignUpBy} from 'compob2b-libs'
import {legacyUrlGuard} from './shared/routing/legacy-url.guard';
import {slugRoutes} from './shared/routing/slug-routes';


/**
 * Карта URL кабинета (SEO-8, ТЗ §4).
 *
 * Кабинет живёт в **корне сайта**: главная — по `/`, публичные разделы без
 * служебных сегментов (`/news`, `/promotions`, `/pages`, `/faq`), приватные — под
 * общим префиксом `/account/*`. Префикса `cabinet/` в конфиге больше нет вовсе.
 *
 * **Legacy-URL здесь НЕ объявлены.** Их уводит серверная мидлварь `server.ts`
 * (301, §5.2) и предзагрузочная перезапись в `main.ts` (CSR-паритет, §6.10).
 * Держать ~15 legacy-роутов в конфиге ради одного хопа сочтено дороже, чем
 * принять два хопа на миграционном корпусе (решение ревизии 8).
 *
 * **У каждой slug-сущности ДВА роута** — `:slug/:key` и `:key` (§5.4). Второй
 * обязателен: в CSR никакого 301 нет, и путь без slug дал бы 404 ещё до того, как
 * сработает нормализация; в SSR он нужен для сущностей с пустым заголовком.
 *
 * **Порядок объявления — часть контракта** (§6.8), закреплён `route-order.spec.ts`:
 * `pages/where-to-buy` до `pages/:id`; в `product.routes.ts` — `:uid/analogs` до
 * `:slug/:uid`.
 */
export const routes: Routes = [
  // Аутентификация — вне layout. Прежние `sign-in` → `/cabinet/sign-in`
  // редирект-обёртки схлопнуты: теперь это и есть настоящие пути.
  {path: 'sign-in', loadComponent: () => import('../auth/sign-in/sign-in.component').then(m => m.CustomerSignInComponent), pathMatch: 'full', canActivate: [GuestGuardService]},
  {path: 'reset', loadComponent: () => import('../auth/reset-component/customer-reset.component').then(m => m.CustomerResetComponent), pathMatch: 'full'},
  {
    path: 'sign-up',
    loadComponent: () =>
      environment.loadedRoutes.signupFormType === SignUpBy.EMAIL
        ? import('../auth/sign-up/by-email/customer-sign-up-by-email.component').then(c => c.CustomerSignUpByEmailComponent)
        : import('../auth/sign-up/by-sms/customer-sign-up.component').then(c => c.CustomerSignUpComponent),
  },
  {
    path: '404',
    loadComponent: () => import('./shared/not-found/not-found.component').then(c => c.NotFoundComponent),
    // `notFound` → server-рендер + HTTP 404 (см. `SsrRouteData`). Сюда уводит глобальный
    // `ErrorHandler` при 404 от бэка, то есть здесь оседают URL удалённых сущностей;
    // без статуса они уходили краулеру как 200 и оставались в индексе soft-404'ами.
    data: {notFound: true},
  },

  {
    path: '',
    loadComponent: () => import('./shared/layout/layout.component').then(m => m.LayoutComponent),
    children: [
      // Главная. Раньше корень редиректил на `/cabinet/dashboard` — теперь дашборд
      // рендерится по `/` напрямую, без хопа.
      {
        path: '',
        pathMatch: 'full',
        loadComponent: () => import('./dashboard/main-component/dashboard.component').then(c => c.DashboardComponent),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {ssr: true, breadcrumb: {label: translations.bc.home}},
      },

      // ---------- публичные разделы ----------
      {
        // Новости. `common` из URL убран (§4.1): список — сам `/news`.
        path: 'news',
        canActivate: [AuthGuardService],
        data: {breadcrumb: {label: translations.bc.news}},
        children: [
          {
            // `breadcrumb.skip` здесь НЕЛЬЗЯ: сервис крошек мержит данные
            // пустопутевого ребёнка В РОДИТЕЛЯ (`mergeWithBaseChildData`), и `skip`
            // протекал на узел `/news`, скрывая уровень «Новости».
            //
            // Смысла в нём и нет: узел с пустым `path` в цепочку не добавляется
            // вовсе (`prepareBreadcrumbList` проверяет `routeConfig.path`).
            // До миграции список жил на `common` — непустой путь, свой узел, и
            // `skip` там был осмысленным. Переезд списка в `''` сделал флаг
            // бесполезным и вредным одновременно.
            path: '',
            data: {ssr: true},
            loadChildren: () => import('./news/common-news-module/common-news.routes').then(m => m.COMMON_NEWS_ROUTES),
          },
          // Дубля guard'а здесь нет намеренно: родитель уже несёт
          // AuthGuardService, а Angular прогоняет гарды на каждом уровне —
          // повтор дал бы два isAuthenticated() на одну навигацию.
          ...slugRoutes('news', {
            loadComponent: () => import('./news/components/news-element/news-element.component').then(c => c.NewsElementComponent),
            data: {ssr: true},
          }),
        ],
      },
      {
        // Было `/ad-campaigns` — рассинхрон «сущность в коде vs слово в URL» (§4.1).
        path: 'promotions',
        loadChildren: () => import('./promotions/promotions.routes').then(m => m.PROMOTIONS_ROUTES),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {ssr: true, breadcrumb: {label: translations.bc.promotions}},
      },
      {
        // Литерал ОБЯЗАН быть объявлен до `pages/:id` (§6.8) — иначе `where-to-buy`
        // распознается как ключ текстовой страницы.
        path: 'pages/where-to-buy',
        loadComponent: () => import('./text-page/components/where-to-buy/where-to-buy.component').then(c => c.WhereToBuyComponent),
        canActivate: [AuthGuardService],
        data: {ssrClientOnly: true},
      },
      {
        path: 'pages',
        loadChildren: () => import('./text-page/text-page.routes').then(m => m.TEXT_PAGE_ROUTES),
        canActivate: [AuthGuardService],
        data: {ssr: true},
      },
      {
        path: 'faq',
        loadChildren: () => import('./faq/faq.routes').then(m => m.FAQ_ROUTES),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {label: translations.titles.faq}},
      },
      // Каталог: ТРИ ПЛОСКИХ роута-соседа, а не родитель с детьми.
      //
      // До миграции `catalog` был листовым роутом. Превращение его в родителя
      // добавляло узел в цепочку крошек — она строится по дереву роутов, — и
      // трейл ломался: третьим уровнем рисовались сырые сегменты пути
      // («telefony-svyaz/9486»). `breadcrumb.skip` на детях не помог: он обрывает
      // сборку ветки целиком, и вместе с лишним узлом исчезал сам `/catalog`.
      //
      // Плоские соседи сохраняют форму дерева: каждый — терминальный узел, цепочка
      // снова двухуровневая («Главная / Подбор товара»), никаких `skip` и подбора
      // ключей не нужно. Тот же приём уже применён к `pages/where-to-buy` рядом с
      // `pages`. breadcrumb-конфигурация у форм ПОЧТИ одинаковая: у корня добавлен
      // `useRouterLinkOnly` — почему именно у него, разобрано ниже.
      //
      // Порядок: сначала форма со slug (три сегмента), потом без (два), потом
      // корень. Конфликта по числу сегментов между ними нет, порядок — для
      // читаемости. Переезд `catalog_id` из query в path — §7.
      // У параметризованных форм `useRouterLinkOnly` НЕ ставим: этот флаг гасит
      // dynamic-store (`breadcrumbs.service.ts:113`), из-за чего терминальный узел
      // всегда получал статическую метку «Подбор товара», а вызов
      // `setWithParams` не находил узла и уходил в else-ветку
      // `updateCurrentBreadcrumbs`, ДОБАВЛЯЯ лишний элемент. Визуально выходило
      // три уровня, но метка и ссылка были перепутаны: «Подбор товара» вело в
      // категорию, а название категории — в корень каталога.
      //
      // Статическая метка остаётся fallback'ом: если заголовок ещё не загружен
      // (или бэк не ответил), крошка покажет «Подбор товара», а не сырой сегмент.
      ...slugRoutes('catalog', {
        loadComponent: () => import('./catalog/catalog-list/catalog-list.component').then(c => c.CatalogListComponent),
        canActivate: [AuthGuardService],
        data: {ssr: true, breadcrumb: {label: translations.bc.catalog}},
      }, 'catalog/'),
      {
        path: 'catalog',
        loadComponent: () => import('./catalog/catalog-list/catalog-list.component').then(c => c.CatalogListComponent),
        canActivate: [AuthGuardService],
        data: {ssr: true, breadcrumb: {label: translations.bc.catalog, useRouterLinkOnly: true}},
      },
      {
        // Флаг ssr ОБЯЗАН стоять здесь, на родителе: `walk()` не заглядывает внутрь
        // `loadChildren`, и объявленный в ленивом файле он был бы для него невидим —
        // раздел уехал бы в CSR пустым шеллом.
        path: 'list',
        loadChildren: () => import('./seo-list-page/seo-list-page.routes').then(m => m.SEO_LIST_PAGE_ROUTES),
        canActivate: [AuthGuardService],
        data: {ssr: true, breadcrumb: {label: translations.bc.listPages}},
      },
      {
        // Порядок форм — внутри product.routes.ts (§6.8).
        path: 'product',
        loadChildren: () => import('./product/product.routes').then(m => m.PRODUCT_ROUTES),
        canActivate: [AuthGuardService],
        data: {ssr: true},
      },
      {
        path: 'cart',
        loadComponent: () => import('./cart/cart-table/cart-table.component').then(c => c.CartTableComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'cart-share/:shareSyncUid',
        loadComponent: () => import('./cart/cart-table/cart-table.component').then(c => c.CartTableComponent),
        canActivate: [AuthGuardService],
      },
      {
        path: 'subscribe-confirm/:code',
        loadComponent: () => import('./subscribe-confirm/subscribe-confirm-component/subscribe-confirm.component').then(m => m.SubscribeConfirmComponent),
        canActivate: [AuthGuardService],
        data: {breadcrumb: {label: 'Подтверждение подписки'}},
      },
      {
        path: 'download/specification/:hash',
        loadComponent: () => import('./download/download-component/download.component').then(m => m.DownloadComponent),
        canActivate: [AuthGuardService],
        data: {breadcrumb: {label: 'Скачать спецификации'}},
      },

      // ---------- приватные разделы: /account/* ----------
      // Индексные редиректы. `pathMatch: 'full'` обязателен — иначе они перехватят
      // и вложенные пути. Объявлены ДО одноимённых lazy-роутов.
      //
      // Два из них ЧИНЯТСЯ (§11.7): раньше `/cabinet/orders` вёл на несуществующий
      // `orders/products`, а `/cabinet/private` — на несуществующий `private/info`.
      // Три заглушки (`complaints`, `files`, `chat`) УДАЛЕНЫ с концами: их таргетов в
      // проекте нет, то есть они делали лишний хоп в тот же `**` → NotFound.
      {path: 'account', redirectTo: '/account/profile', pathMatch: 'full'},
      {path: 'account/orders', redirectTo: '/account/orders/specifications', pathMatch: 'full'},
      {path: 'account/reports', redirectTo: '/account/reports/orders', pathMatch: 'full'},
      {path: 'account/finances', redirectTo: '/account/finances/dept', pathMatch: 'full'},

      {
        // Персональные новости клиента. Переехали из `/news/client/:id` под
        // `/account/` (§11.4): в новой карте `news/:slug/:id` — тоже два сегмента
        // после `/news`, то есть формы коллидировали бы, и работоспособность
        // держалась бы на порядке объявления. Здесь коллизия снята физически.
        //
        // ssrClientOnly: страница за AuthGuard, SSR-рендер под анон-токеном отдал бы
        // 401-empty HTML.
        path: 'account/news/:id',
        loadComponent: () => import('./news/components/news-element/news-element.component').then(c => c.NewsElementComponent),
        canActivate: [AuthGuardService],
        data: {ssrClientOnly: true, breadcrumb: {skip: true}},
      },
      {
        path: 'account/orders',
        loadChildren: () => import('./orders/orders.routes').then(m => m.ORDERS_ROUTES),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {skip: true}},
      },
      {
        path: 'account/reports',
        loadChildren: () => import('./reports/reports.routes').then(m => m.REPORTS_ROUTES),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {skip: true}},
      },
      {
        path: 'account/finances',
        loadChildren: () => import('./finances/finances.routes').then(m => m.FINANCES_ROUTES),
        canActivate: [AuthGuardService, AuthGuardMenuService],
        data: {breadcrumb: {skip: true}},
      },
      {
        path: 'account/notifications',
        loadChildren: () => import('./notifications-module/notifications.routes').then(m => m.NOTIFICATIONS_ROUTES),
        canActivate: [AuthGuardService],
        data: {access: environment.notificationCustomerAccess},
      },
      {
        // Прежний `private.routes.ts` схлопнут: его две записи стали явными
        // роутами. Через lazy-родителя `account` это делать нельзя — при промахе
        // по дочерним путям поведение Angular зависит от backtracking'а, а рядом
        // живут `account/orders`, `account/reports` и т.д.
        path: 'account/profile',
        loadChildren: () => import('./private/account-module/account.routes').then(m => m.ACCOUNT_ROUTES),
        canActivate: [AuthGuardService, AuthGuardCustomerService],
      },
      {
        path: 'account/company',
        loadComponent: () => import('./private/company-module/components/account-common/company.component').then(m => m.CompanyComponent),
        canActivate: [AuthGuardService, AuthGuardCustomerService],
        data: {breadcrumb: {label: translations.bc.company}},
      },

      {
        path: '404',
        loadComponent: () => import('./shared/not-found/not-found.component').then(c => c.NotFoundComponent),
        canActivate: [AuthGuardService],
      },
      {
        // Неизвестный путь → рендерим NotFound ПО МЕСТУ (URL сохраняется, страница
        // в обычном лейауте), без редиректа на /404. Реальный HTTP 404 даёт
        // server-route `**`→{Server, status:404} (см. app.routes.server.ts).
        //
        // legacyUrlGuard — слой C паритета CSR (§6.10): если legacy-путь всё-таки
        // дошёл до роутера внутренней навигацией (значит в §5.5 забыли литерал),
        // guard уводит на новый путь вместо показа 404 и предупреждает в dev.
        path: '**',
        canActivate: [legacyUrlGuard],
        loadComponent: () => import('./shared/not-found/not-found.component').then(c => c.NotFoundComponent),
        // skip: иначе xng-breadcrumb рисует литеральный сегмент пути «**» в крошках.
        data: {breadcrumb: {skip: true}},
      },
    ],
  },
];
